/**
 * engine/motor.ts — núcleo de cálculo do Fincast.
 *
 * Cópia LITERAL de fincast-viabilidade-v67.html, linhas 1420-1591:
 *   calcBase (1420-1474) · calcEstatico (1476-1521) · calcFluxo (1522-1574) · calcViab (1575-1591)
 *
 * Mesmas fórmulas, mesma ordem de operações, mesmos arredondamentos, mesmos nomes de campo
 * no objeto de retorno. Onde o protótipo faz algo estranho (guarda ausente, parâmetro morto,
 * constante mock), o estranho foi PRESERVADO e comentado.
 *
 * Diferenças de assinatura (e só de assinatura):
 *   - `calcBase` recebe `premissas` como 3º parâmetro (no v67 lia a global `PREMISSAS`).
 *   - `calcFluxo` recebe `cfg` opcional. Desde 15/10/2026 o fluxo NÃO é mais cópia do v67: segue o
 *     memorial de cálculo e mora em `engine/fluxo.ts` (ver a nota em `calcFluxo`, abaixo).
 *   - `calcViab` recebe `fmt` opcional (formatadores pt-BR), default idêntico ao v67.
 * Nenhuma função escreve em nada que veio de fora.
 */

import { teto, ymOf } from './datas';
import { demTemDados, vendasMensais } from './demanda';
import { estoqueDemReal, fsfPorUnidade, repasseDaUnidade, terrenoGarFrac, terrenoGarM2, tetoInformadoPJ } from './garantias';
import { FORMATADORES_PADRAO, type FormatadoresViab } from './formato';
/* 15/10/2026: o fluxo mensal pelo memorial. Sem ciclo: fluxo.ts não importa daqui. */
import { CONFIG_FLUXO_PADRAO, calcFluxoMemorial, type ConfigFluxo } from './fluxo';
import type {
  Empreendimento, IghEstatico, Lev, LimitesDespesa, MotivoViab, PremissasMacro,
  ResultadoBase, ResultadoEstatico, ResultadoFluxo, ResultadoViab, Ym,
} from './tipos';

export { CONFIG_FLUXO_PADRAO };
export type { ConfigFluxo };

/* ============================================================================
 * calcBase — motor com tabela mensal (v67 1420-1474)
 * ========================================================================== */

/**
 * @param b         empreendimento ativo (já escopado por tenant pelo repositório)
 * @param lev       cenário/alavancagem
 * @param premissas ex-global `PREMISSAS`. Só `premissas.desp` é lido, e só para
 *                  `lim`/`limErr` (faixas de despesa). Sem default de propósito: no
 *                  produto multiempresa a faixa é configuração do tenant, não do browser.
 */
export function calcBase(b: Empreendimento, lev: Lev, premissas: PremissasMacro): ResultadoBase {
  const U = b.unidades, VGV = b.VGV, Laudo = b.ctoLaudo, T = b.valorTerreno, isMCMV = (b.programa === 'MCMV');
  const d = premissas.desp;
  const lim: LimitesDespesa = {
    df: [d.dfMin / 100 * Laudo, d.dfMax / 100 * Laudo],
    dc: [d.dcMin / 100 * VGV, d.dcMax / 100 * VGV],
    ds: [0, d.segMax / 100 * Laudo],
    dl: [0, d.legMax / 100 * VGV],
  };
  /* comportamento preservado do protótipo: as 4 despesas são lidas SEM guarda `||0` */
  const df = b.despFin, dc = b.despCom, ds = b.despSeg, dl = b.despLeg;
  /* CAL-2c (12/10/2026): folga de R$ 0,50 no aviso "Extrapolou o limite" (EL-049 a EL-052),
     decisão D6 — a mesma convenção das comparações em reais do motor (hGar, hLGC, IGH). O limite
     é % × laudo/VGV: sai com resíduo binário (3,5% × 7.000.000 = 245000.00000000003) ou com
     centavos que a tela arredonda (R$ 356.172,85 aparece "R$ 356.173"), e digitar o valor
     mostrado avisava. `lim` (o que a mensagem exibe) não muda. DIVERGÊNCIA INTENCIONAL DA v67
     só na faixa de R$ 0,50; nenhum caso de tests/golden cai nela. */
  const limErr = { df: df < lim.df[0] - 0.5 || df > lim.df[1] + 0.5, dc: dc < lim.dc[0] - 0.5 || dc > lim.dc[1] + 0.5, ds: ds > lim.ds[1] + 0.5, dl: dl > lim.dl[1] + 0.5 };
  const CTE = Laudo + df + dc + ds + dl + T, CTO = isMCMV ? (CTE - df - T - dc) : (CTE - df - T);
  const preco = U ? VGV / U : 0, fracao = U ? T / U : 0;
  /* v43 item 10: mínimo de repassadas (sem EVV) = 20% do total; item 9 já usava 15%/30% em vendExig */
  const vendExig = lev.temEVV ? teto(0.15 * U) : teto(0.30 * U), finExig = lev.temEVV ? 1 : teto(0.20 * U);
  /* item 7/Q2: mínimo = maior entre input manual e a exigência de demanda */
  const salesMin = Math.max(lev.unidVendDem || 0, vendExig), finMin = Math.max(lev.unidFinDem || 0, finExig);
  const tetoPJ = Math.min(0.60 * VGV, 0.85 * CTO);
  /* EL-059 (item 7a); v49 EL-154: FSF por unidade quando a tabela de demanda tem dados.
     comportamento preservado do protótipo: no ramo agregado NÃO há `||0` (≠ calcEstatico) */
  const valorFinDem = demTemDados(lev) ? fsfPorUnidade(b, lev)
    : (lev.unidFinDem * repasseDaUnidade(preco, lev.pctFin, fracao));   /* v67 + desconto do terreno (31/07/2026) */
  /* acumulados da tabela mensal (Premissas mensais) — as vendas que valem (20/10/2026, "um OU outro":
     com o Detalhamento preenchido, a contagem das unidades; `vendasMensais`) */
  const meses = vendasMensais(lev);
  const acumAtIdx = (i: number) => { let ac = lev.pls0Pct || 0; for (let k = 0; k <= i && k < meses.length; k++) ac += (meses[k].obra || 0); return ac; };   /* % acum. */
  const soldCum = (i: number) => { let s = 0; for (let k = 0; k <= i && k < meses.length; k++) s += (meses[k].vendFin || 0) + (meses[k].vendNao || 0); return s; };
  const finCum = (i: number) => { let s = 0; for (let k = 0; k <= i && k < meses.length; k++) s += (meses[k].vendFin || 0); return s; };
  /* item 8: "demanda batida" = 1º mês em que unidades comercializadas e repassadas atingem os mínimos.
     Os critérios de garantias≥CTO MCV e IGH≥1,30×VGV ficam como MOCK/fora do cálculo por ora
     (decisão do usuário — Q3): não travam o mês da demanda, para não zerar o PJ (PJ = CTO − garantias). */
  let idxBatida = -1;
  for (let i = 0; i < meses.length; i++) { if (soldCum(i) >= salesMin && finCum(i) >= finMin) { idxBatida = i; break; } }
  const ymBatida: Ym | null = idxBatida >= 0 ? meses[idxBatida].ym : null;
  const demandaBatida = idxBatida >= 0;
  /* datas-limite (item 8) */
  const ymNorm: Ym | null = lev.dataContratacao ? (ymOf(lev.dataContratacao) as number) + 6 + (lev.extensaoDemanda || 0) : null;   /* item 17: prazo normativo = 6 + extensão autorizada */
  const ymDesejada: Ym | null = lev.dataDemandaDesejada ? ymOf(lev.dataDemandaDesejada) : null;   /* item 10: cumprimento desejado (soft/semáforo) */
  const dataLimiteExcede = (ymDesejada != null && ymNorm != null && ymDesejada > ymNorm);
  const ymDeadline = ymNorm;   /* prazo normativo (hard) = 6 meses após a contratação */
  /* data-limite dos cálculos (Q3): menor entre batida e o prazo */
  const ymLimite: Ym | null = (ymBatida != null && ymDeadline != null) ? Math.min(ymBatida, ymDeadline) : (ymBatida != null ? ymBatida : ymDeadline);
  /* mês-alvo da demanda: a batida; se nunca bate, último mês */
  const idxAlvo = demandaBatida ? idxBatida : (meses.length - 1);
  /* EL-062 = acum.% do mês da demanda × CTO MCV (item 7d) */
  const acumObraPct = idxAlvo >= 0 ? acumAtIdx(idxAlvo) : 0;
  const valorObra = (acumObraPct / 100) * CTO;
  const ymObra: Ym | null = idxAlvo >= 0 ? meses[idxAlvo].ym : null;
  /* EL-060 terreno como garantia — frações das financiadas na demanda até o mês-alvo (item 7c) */
  const finCumLimite = idxAlvo >= 0 ? finCum(idxAlvo) : 0;
  /* v54 item 11: Fração Ideal do EL-154; fallback m²/uniforme */
  const _tgF = terrenoGarFrac(b, lev); const _tgM2 = terrenoGarM2(b, lev);
  const valorTerrenoGar = (_tgF != null) ? _tgF : (_tgM2 != null ? _tgM2 : (lev.terrenoGarantia ? fracao * finCumLimite : 0));
  const obraAtingida = demandaBatida;   /* compat com o motivo de viabilidade */
  /* EL-061 PJ calculado (item 7) = CTO MCV − garantias, com teto no PJ máximo.
     comportamento preservado do protótipo: `lev.aporte` sem guarda `||0` (≠ calcEstatico) */
  const garantiasTotal = valorFinDem + lev.aporte + valorTerrenoGar + valorObra;
  const pjBruto = CTO - garantiasTotal;
  const pjCapped = pjBruto > tetoPJ;
  /* v45 item 11: teto do LGC só quando "PJ limitado ao LGC disponível" = Sim */
  const pjLGCmax = (lev.lgcPct > 0) ? ((b.lgcDisponivel || 0) / (lev.lgcPct / 100)) : Infinity;
  const lgcCap = lev.pjLimitadoLGC ? pjLGCmax : Infinity;
  /* EL-192 (09/10/2026): o "PJ limite" do empreendimento entra como 4º teto. Vazio = Infinity,
     e o resultado é byte a byte o de antes. `pjCapped` continua falando só do teto normativo. */
  const PJ = Math.max(0, Math.min(pjBruto, lgcCap, tetoPJ, tetoInformadoPJ(b)));
  /* antecipação em % (item 5) — limite 10% do PJ */
  const antecPct = lev.antecipPct || 0, antecExcede = antecPct > 10, antecMax = 0.10 * PJ;
  const antecip = Math.min(antecPct, 10) / 100 * PJ;
  const tomadaLGC = PJ * (lev.lgcPct / 100), saldoLGC = (b.lgcDisponivel || 0) - tomadaLGC;
  const ymDem = ymBatida;
  const demandaForaPrazo = (ymBatida == null) || (ymDeadline != null && ymBatida > ymDeadline);
  return { U, VGV, CTE, CTO, preco, fracao, lim, limErr, vendExig, finExig, salesMin, finMin, tetoPJ, valorFinDem, valorObra, acumObraPct, obraAtingida, demandaBatida, ymObra, valorTerrenoGar, finCumLimite, PJ, pjBruto, pjCapped, antecPct, antecMax, antecExcede, antecip, tomadaLGC, saldoLGC, ymDem, ymBatida, ymNorm, ymDesejada, ymDeadline, ymLimite, dataLimiteExcede, demandaForaPrazo, garantiasTotal, isMCMV };
}

/* ============================================================================
 * calcEstatico — motor SEM tabela mensal (v67 1476-1521, "v23")
 * ========================================================================== */

/**
 * Composição de garantias na data desejada, sem tabela mensal.
 * Já era 100% puro no protótipo (não lê `PREMISSAS`: não calcula lim/limErr).
 *
 * ⚠ Duplica ~15 linhas de preâmbulo de `calcBase`, mas os FALLBACKS de terreno divergem
 * DE PROPÓSITO (`calcBase` usa `finCumLimite`, vindo do fluxo; aqui usa `lev.unidFinDem`).
 * Não unificar.
 */
export function calcEstatico(b: Empreendimento, lev: Lev): ResultadoEstatico {
  const U = b.unidades, VGV = b.VGV, Laudo = b.ctoLaudo, T = b.valorTerreno, isMCMV = (b.programa === 'MCMV');
  const df = b.despFin, dc = b.despCom, ds = b.despSeg, dl = b.despLeg;
  const CTE = Laudo + df + dc + ds + dl + T, CTO = isMCMV ? (CTE - df - T - dc) : (CTE - df - T);
  const preco = U ? VGV / U : 0, fracao = U ? T / U : 0;
  const vendExig = lev.temEVV ? teto(0.15 * U) : teto(0.30 * U), finExig = lev.temEVV ? 1 : teto(0.20 * U);
  const salesMin = Math.max(lev.unidVendDem || 0, vendExig), finMin = Math.max(lev.unidFinDem || 0, finExig);
  const tetoPJ = Math.min(0.60 * VGV, 0.85 * CTO);
  /* componentes de garantia — todos calculáveis sem o fluxo temporal */
  /* FSF; v49 EL-154. O `máx(0, preço×% − fração do terreno)` é a fórmula do "Preço Médio
     de Repasse" da planilha da Caixa (MO 3041), conferida no caso VERUS — ver a nota em
     `fsfPorUnidade`. Era `preço × %` puro, e superestimava o FSF em (unidades × fração). */
  const valorFinDem = demTemDados(lev) ? fsfPorUnidade(b, lev)
    : ((lev.unidFinDem || 0) * repasseDaUnidade(preco, lev.pctFin, fracao));
  /* v54 item 11: Fração Ideal do EL-154; fallback m²/uniforme */
  const _tgF = terrenoGarFrac(b, lev); const _tgM2 = terrenoGarM2(b, lev);
  const valorTerrenoGar = (_tgF != null) ? _tgF : (_tgM2 != null ? _tgM2 : (lev.terrenoGarantia ? fracao * (lev.unidFinDem || 0) : 0));
  const valorObra = ((lev.obraGarPct || 0) / 100) * CTO;                                 /* EL-121: % informado × CTO MCV */
  const aporte = lev.aporte || 0;
  const garantiasTotal = valorFinDem + aporte + valorTerrenoGar + valorObra;
  const pjLGCmax = (lev.lgcPct > 0) ? ((b.lgcDisponivel || 0) / (lev.lgcPct / 100)) : Infinity;   /* item 4: teto de PJ pela LGC */
  const lgcCap = lev.pjLimitadoLGC ? pjLGCmax : Infinity;   /* v45 item 11 */
  /* EL-192 (09/10/2026): 4º teto, o "PJ limite" informado — ver `tetoInformadoPJ` */
  const PJ = Math.max(0, Math.min(CTO - garantiasTotal, lgcCap, tetoPJ, tetoInformadoPJ(b)));
  const pjRemanescente = PJ;                                                             /* item 4: PJ a contratar */
  const gapCTO = Math.max(0, CTO - garantiasTotal - PJ);                                 /* descoberto após garantias + PJ */
  const antecip = Math.min(lev.antecipPct || 0, 10) / 100 * PJ;
  const tomadaLGC = PJ * ((lev.lgcPct || 0) / 100), saldoLGC = (b.lgcDisponivel || 0) - tomadaLGC;
  /* datas (referência: desejada; normativo = contratação + 6m) */
  const ymDesejada: Ym | null = lev.dataDemandaDesejada ? ymOf(lev.dataDemandaDesejada) : null;
  const ymNorm: Ym | null = lev.dataContratacao ? (ymOf(lev.dataContratacao) as number) + 6 + (lev.extensaoDemanda || 0) : null;   /* item 17 */
  const dataLimiteExcede = (ymDesejada != null && ymNorm != null && ymDesejada > ymNorm);
  /* item (v34): IGH do EL-129 — SELF-CONTAINED na aba EL-093 */
  const ymDmed = ymDesejada;                                                 /* EL-065 = data de referência */
  const igh_medAc = (lev.obraGarPct || 0) / 100;                             /* EL-121 % = medição acumulada */
  const igh_GTO = Math.max(0, (1 - igh_medAc) * CTO);                        /* (1 − EL-121%) × EL-082 CTO MCV */
  const igh_SMPF = valorFinDem;                                              /* Saldo Mut. PF = FSF cheio */
  const igh_SDA = 0;                                                         /* v35: Saldo Devedor Atual = 0 no instante da demanda — evita dobrar o PJ */
  /**
   * ⚠ O DENOMINADOR DO IGH É O **PJ**, não `GTO − FSF` — corrigido em 01/08/2026.
   *
   * A planilha da Caixa (MO 3041, `D174`) escreve a regra como teto do empréstimo:
   * "Valor Máximo do Financiamento – IGH 130%" = garantia ÷ 1,30. Ou seja, o que o índice
   * protege é **o dinheiro que o banco põe**.
   *
   * O motor usava `GTO − FSF`: o custo que falta menos a parte dos compradores. Isso supõe
   * que tudo que os compradores não cobrem é EMPRESTADO — e esquece que terreno, aporte e
   * obra executada também cobrem. A conta da diferença, com `obra = o × CTO` cancelando:
   *
   *     (GTO − FSF) − PJ = terreno + aporte
   *
   * O sintoma que qualquer usuário sentiria: **aumentar o aporte não melhorava o IGH**,
   * embora reduza o empréstimo e, portanto, a exposição do banco. Na Caixa, melhora.
   *
   * ⚠ USA O PJ **DEPOIS DAS TRAVAS** (teto de LGC, teto de 60%/85% e, desde 09/10/2026, o
   * "PJ limite" do empreendimento — EL-192), por decisão de 01/08/2026: é o empréstimo real,
   * que é o que a Caixa mede. Quando a trava corta e a operação fica descoberta, quem
   * denuncia é o critério "Garantias ≥ CTO", que reprova sozinho — fazer o IGH reprovar de
   * novo pela mesma causa seria ruído, não proteção. Consequência que vale dizer na tela:
   * um "PJ limite" menor BAIXA o mínimo do IGH, mas não resolve a operação — só desloca a
   * reprovação para "Garantias ≥ CTO".
   *
   * `igh_GTO` e `igh_SMPF` continuam sendo devolvidos: a memória de cálculo (EL-184) os
   * exibe para mostrar de onde vem a necessidade de financiamento.
   */
  const igh_SMPJ = PJ;
  const ighMin = (igh_SMPJ + igh_SDA) * 1.30;                                /* Valor Mínimo em Estoque = (PJ + SD) × 1,3 */
  const precoMed = U ? VGV / U : 0;                                          /* avaliação média = EL-083 ÷ EL-084 */
  /* Parte A do IGH (29/07/2026): com a tabela EL-154 preenchida, o estoque passa a ser o
     VALOR REAL de quem ficou (+ vagas vinculadas + vagas soltas) em vez de contagem ×
     média. A RÉGUA DE SAÍDA é a mesma (EL-057 ∪ EL-114, ver `_saiuEstoqueDem`); muda o
     VALOR. Tabela vazia → `null` → contagem × média, byte a byte como antes. */
  const _estReal = estoqueDemReal(lev);
  const igh_unidEstoque = _estReal ? _estReal.unidades
    : Math.max(0, U - (lev.unidFinDem || 0) - (b.permutadas || 0));           /* EL-084 − EL-057 − EL-114 */
  const ighReal = _estReal ? _estReal.valor : igh_unidEstoque * precoMed;     /* Garantia em Estoque (realizado) */
  const ighCumpre = ighReal >= ighMin - 0.5;
  const igh: IghEstatico = { GTO: igh_GTO, SMPF: igh_SMPF, SMPJ: igh_SMPJ, SDA: igh_SDA, minEstoque: ighMin, estoque: ighReal, medAc: igh_medAc, unidEstoque: igh_unidEstoque, cumpre: ighCumpre, temUnid: !!_estReal, estatico: true };
  /* gates — duros (vermelho) e internos (amarelo); v45 fix: tolerância 0,5 no LGC */
  const hGar = (garantiasTotal + PJ) >= CTO - 0.5, hVend = (lev.unidVendDem || 0) >= salesMin, hRep = (lev.unidFinDem || 0) >= finMin, hLGC = tomadaLGC <= (b.lgcDisponivel || 0) + 0.5, hIGH = igh.cumpre;
  const hardOk = hGar && hVend && hRep && hLGC && hIGH;   /* item 16 (v26): IGH volta ao gate da tela estática */
  const sObra = (lev.obraGarPct || 0) >= (lev.minObraPct || 0);
  const softOk = sObra && !dataLimiteExcede;
  const color = !hardOk ? 'red' : (!softOk ? 'yellow' : 'green');
  return { CTE, CTO, preco, fracao, tetoPJ, vendExig, finExig, salesMin, finMin, valorFinDem, valorTerrenoGar, valorObra, aporte, garantiasTotal, PJ, pjRemanescente, gapCTO, antecip, tomadaLGC, saldoLGC, ighReal, ighMin, igh, ymDmed, ymDesejada, ymNorm, dataLimiteExcede, color, p: { hGar, hVend, hRep, hLGC, hIGH, sObra } };
}

/* ============================================================================
 * calcFluxo — fluxo mês a mês, pelo MEMORIAL DE CÁLCULO (15/10/2026)
 * ========================================================================== */

/**
 * ⚠ FLUXO PELO MEMORIAL (lote 1, 15/10/2026) — a QUARTA divergência deliberada do v67 no fluxo.
 *
 * Pedido literal (14/10/2026): "Na aba fluxo na parte de analise de demanda eu preciso alterar toda a
 * tabela a parte de FSF / Fração Terreno / Receitas Financeiras / Emprestimo PJ / Liberação Garantia
 * Aporte / Amortização PJ / Garantia Aporte e Despesas Financersa […] as alterações que precisam entrar
 * esta aqui em @"Memorial de calculo FinCast.docx"". Decisões D1–D11 e padrões P1–P10 no Registro.md.
 *
 * O corpo mora em `engine/fluxo.ts` (`calcFluxoMemorial`); aqui fica a porta de sempre, com a mesma
 * assinatura `(b, lev, c, cfg?)` — o simulador chama com 3 argumentos (tests/porte-literal) e a ponte
 * injeta as taxas de juros em `cfg`. As regras do lote 4 (10/10/2026) e de 11/10/2026 sobre QUANTAS
 * unidades formam a demanda e quais entram em cada mês continuam valendo — em `unidadesDoFluxo`.
 *
 * @param c   saída de `calcBase` (na tabela de cada barra, com o mês, a antecipação e o terreno da barra).
 */
export function calcFluxo(
  b: Empreendimento,
  lev: Lev,
  c: ResultadoBase,
  cfg: ConfigFluxo = CONFIG_FLUXO_PADRAO,
): ResultadoFluxo | null {
  return calcFluxoMemorial(b, lev, c, cfg);
}

/* ============================================================================
 * calcViab — os 5 motivos de viabilidade (v67 1575-1591)
 * ========================================================================== */

/**
 * ⚠ MISTURA REGRA E APRESENTAÇÃO — e assim foi extraída, porque nesta fatia o motor não
 * pode mudar de comportamento. `motivos[].ok` e `viavel` são puros; `motivos[].m` e
 * `.kpi` são frases pt-BR formatadas. Quem só precisa da regra deve ler `ok`/`viavel` e
 * ignorar as strings.
 *
 * @param _F  PARÂMETRO MORTO — nunca usado no corpo (igual ao protótipo); os 3 chamadores
 *            do v67 passam mesmo assim. Mantido na posição por compatibilidade.
 * @param fmt formatadores injetáveis (default = os do protótipo, dependentes do ICU).
 */
export function calcViab(
  b: Empreendimento,
  lev: Lev,
  c: ResultadoBase,
  _F?: ResultadoFluxo | null,
  fmt: FormatadoresViab = FORMATADORES_PADRAO,
): ResultadoViab {
  const { moeda0, num, ymLabel } = fmt;
  const cut = c.ymLimite;
  const obraFisica = (ym: Ym) => ((lev.pls0Pct || 0) + lev.meses.reduce((s, m) => s + (m.ym <= ym ? (m.obra || 0) : 0), 0));
  const vendas = vendasMensais(lev);   /* 20/10/2026: as vendas que valem ("um OU outro") */
  const vendasCum = (ym: Ym) => vendas.reduce((s, m) => s + (m.ym <= ym ? ((m.vendFin || 0) + (m.vendNao || 0)) : 0), 0);
  const finCum = (ym: Ym) => vendas.reduce((s, m) => s + (m.ym <= ym ? (m.vendFin || 0) : 0), 0);
  const oF = cut != null ? obraFisica(cut) : 0, vC = cut != null ? vendasCum(cut) : 0, fC = cut != null ? finCum(cut) : 0;
  /* motivos carregam kpi curto com atingido/mín; item 6: unidades comercializadas explícitas.
     A ORDEM dos 5 motivos faz parte do congelamento — não reordenar. */
  const motivos: MotivoViab[] = [
    { t: 'Demanda batida dentro do prazo', ok: c.demandaBatida && !c.demandaForaPrazo, m: c.demandaBatida ? `Demanda batida em ${c.ymBatida != null ? ymLabel(c.ymBatida) : '—'}${c.demandaForaPrazo ? ` · ⚠ após o limite ${c.ymDeadline != null ? ymLabel(c.ymDeadline) : ''}` : ` · garantias ${moeda0(c.garantiasTotal)}`}` : 'Unidades insuficientes na tabela para bater a demanda', kpi: c.demandaBatida ? `Batida ${c.ymBatida != null ? ymLabel(c.ymBatida) : ''}` : 'Não batida' },
    /* CAL-2b (12/10/2026): folga de resíduo binário da soma da obra (1e-9 ponto percentual,
       decisão D5) — 2,9 + 7,3 + 4,1 + 0,7 dá 14.999999999999998 e reprovava "15,0% vs mínimo
       15%". Só o resíduo: 14,96% continua reprovado. SEM `|| 0` de propósito, para manter a
       semântica da v67 (mínimo null aprova; undefined → NaN → reprova). DIVERGÊNCIA INTENCIONAL
       DA v67 só nessa faixa; nenhum caso de tests/golden cai nela. */
    { t: 'Mínimo de obra executada até a demanda', ok: oF >= lev.minObraPct - 1e-9, m: `Executado ${oF.toFixed(1).replace('.', ',')}% vs mínimo ${lev.minObraPct}%`, kpi: `Obra ${oF.toFixed(1).replace('.', ',')}% (mín ${lev.minObraPct}%)` },
    /* CAL-2 (12/10/2026): mesma folga de R$ 0,50 do hLGC (calcEstatico) e da barra (igh.ts,
       serieGarantias) — o teto do PJ divide pela taxa e a tomada multiplica de volta; o resíduo
       binário (1000000.0000000001) reprovava. DIVERGÊNCIA INTENCIONAL DA v67, que comparava sem
       folga: nenhum caso de tests/golden cai na faixa (0; 0,5]. `|| 0` como no hLGC. Prova em
       tests/folga-arredondamento.test.ts. */
    { t: 'LGC disponível × tomado', ok: c.tomadaLGC <= (b.lgcDisponivel || 0) + 0.5, m: `Tomada ${moeda0(c.tomadaLGC)} vs disponível ${moeda0(b.lgcDisponivel)}`, kpi: `LGC ${moeda0(c.tomadaLGC)} (disp ${moeda0(b.lgcDisponivel)})` },
    { t: 'Unidades comercializadas até a data limite', ok: vC >= c.salesMin, m: `Comercializadas ${num(vC)} vs exigidas ${num(c.salesMin)}${c.demandaForaPrazo ? ' · ⚠ fora do prazo' : ''}`, kpi: `Comercializadas ${num(vC)} (mín ${num(c.salesMin)})` },
    { t: 'Unidades repassadas até a data limite', ok: fC >= c.finMin, m: `Repassadas ${num(fC)} un. vs exigidas ${num(c.finMin)}`, kpi: `Repassadas ${num(fC)} (mín ${num(c.finMin)})` },
  ];
  const viavel = motivos.every(x => x.ok) && !c.demandaForaPrazo;
  return { motivos, viavel, oF, vC, fC };
}
